'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { harvestService } from '@/lib/services/farmService';

interface HarvestFields {
  bedId: string;
  harvestDate: string;
  wetWeightKg: string;
  dryWeightKg: string;
  volumeL: string;
  qualityGrade: string;
  moisturePct: string;
  batchNotes: string;
}

const beds = ['Bed 01','Bed 02','Bed 03','Bed 04','Bed 05','Bed 06','Bed 07','Bed 08'];

export default function HarvestForm({ onSaved }: { onSaved?: () => void }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HarvestFields>({ defaultValues: { harvestDate: new Date().toISOString().split('T')[0] } });

  const onSubmit = async (data: HarvestFields) => {
    if (!user) {
      toast.error('You must be signed in to save records');
      return;
    }
    setLoading(true);
    try {
      const result = await harvestService.create({
        userId: user.id,
        bedId: data.bedId,
        harvestDate: data.harvestDate,
        wetWeightKg: parseFloat(data.wetWeightKg),
        dryWeightKg: data.dryWeightKg ? parseFloat(data.dryWeightKg) : undefined,
        volumeL: data.volumeL ? parseFloat(data.volumeL) : undefined,
        qualityGrade: data.qualityGrade,
        moisturePct: data.moisturePct ? parseFloat(data.moisturePct) : undefined,
        batchNotes: data.batchNotes || undefined,
      });
      if (result) {
        setSaved(true);
        toast.success('Harvest record saved');
        setTimeout(() => setSaved(false), 3000);
        reset({ harvestDate: new Date().toISOString().split('T')[0] });
        onSaved?.();
      } else {
        toast.error('Failed to save harvest record. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save harvest record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-base font-bold text-foreground">Record Harvest</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Logged by: {user?.user_metadata?.full_name || user?.email || 'You'}</p>
      </div>

      {saved && (
        <div className="mx-4 mt-4 flex items-center gap-2 bg-success-bg border border-success/20 rounded-lg px-3 py-2.5">
          <CheckCircle2 size={15} className="text-success" />
          <span className="text-sm font-medium text-success">Harvest record saved!</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Bed <span className="text-danger">*</span>
            </label>
            <select
              className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.bedId ? 'border-danger' : 'border-border'}`}
              {...register('bedId', { required: 'Select a bed' })}
            >
              <option value="">Select…</option>
              {beds.map((b) => <option key={`hv-bed-${b}`} value={b}>{b}</option>)}
            </select>
            {errors.bedId && <p className="mt-1 text-xs text-danger">{errors.bedId.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.harvestDate ? 'border-danger' : 'border-border'}`}
              {...register('harvestDate', { required: 'Date required' })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Wet Weight (kg) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums ${errors.wetWeightKg ? 'border-danger' : 'border-border'}`}
            placeholder="e.g. 22.5"
            {...register('wetWeightKg', { required: 'Wet weight required', min: { value: 0.1, message: 'Min 0.1 kg' } })}
          />
          {errors.wetWeightKg && <p className="mt-1 text-xs text-danger">{errors.wetWeightKg.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Quality Grade <span className="text-danger">*</span>
          </label>
          <div className="flex gap-3">
            {(['A', 'B', 'C'] as const).map((g) => (
              <label key={`grade-${g}`} className="flex-1 relative">
                <input
                  type="radio"
                  value={g}
                  className="sr-only peer"
                  {...register('qualityGrade', { required: 'Select a grade' })}
                />
                <div className={`w-full py-3.5 rounded-xl border-2 text-center text-base font-bold cursor-pointer transition-all peer-checked:border-primary peer-checked:bg-secondary peer-checked:text-primary border-border text-muted-foreground ${g === 'A' ? 'peer-checked:text-success peer-checked:border-success peer-checked:bg-success-bg' : g === 'B' ? '' : 'peer-checked:text-warning-foreground peer-checked:border-warning peer-checked:bg-warning-bg'}`}>
                  {g}
                </div>
              </label>
            ))}
          </div>
          {errors.qualityGrade && <p className="mt-1 text-xs text-danger">{errors.qualityGrade.message}</p>}
        </div>

        {/* Optional fields */}
        <button
          type="button"
          onClick={() => setShowOptional((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-primary font-medium py-1"
        >
          <ChevronDown size={15} className={`transition-transform ${showOptional ? 'rotate-180' : ''}`} />
          {showOptional ? 'Hide' : 'Add'} optional details
        </button>

        {showOptional && (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Dry Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                  placeholder="e.g. 18.0"
                  {...register('dryWeightKg')}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Volume (L)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                  placeholder="e.g. 28.0"
                  {...register('volumeL')}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Moisture (%)</label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                placeholder="e.g. 45"
                {...register('moisturePct')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Batch Notes</label>
              <textarea
                rows={3}
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Colour, texture, odour, or quality concerns…"
                {...register('batchNotes')}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base hover:opacity-90 transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : '✓ Save Harvest Record'}
        </button>
      </form>
    </div>
  );
}